// Stored payloads, and where they hide.
//
// Story VC-023. `agent_runtime_lld.md` §30.6, §30.8.
//
// # Why a walk rather than a field
//
// §30.8 describes the indirection as the record's whole payload, and for a
// record-level payload it is. The frames a panel actually renders carry it
// *nested*: an `inference.started` puts one under `system` and one under
// every `messages[].content[].text`, because each of those is redacted on its
// own against `max_payload_bytes`. So a reader looking only at the top of the
// frame finds nothing to resolve and shows a reference it could have fetched.

/** The adjacent tag `DebugPayload` serialises with (`payload.rs`). */
const STORED = "stored";

/**
 * Whether a value is a stored-payload reference.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isStored(value) {
  return (
    typeof value === "object" &&
    value !== null &&
    value.state === STORED &&
    typeof value.value?.blob_id === "string"
  );
}

/**
 * Every blob id referenced anywhere in a frame, in encounter order.
 *
 * Deduplicated: one payload may be referenced twice — a retried pass replays
 * the same system prompt — and fetching it twice would double the cost of
 * opening a frame for no new information.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function storedBlobIds(value) {
  const found = [];
  walk(value, (reference) => {
    if (!found.includes(reference.value.blob_id)) {
      found.push(reference.value.blob_id);
    }
  });
  return found;
}

/**
 * The same value with every resolved reference replaced by its content.
 *
 * A copy, never a mutation: the frame is what the stream delivered and a
 * panel that edited it in place would make the arrival log disagree with
 * itself after a click.
 *
 * A reference whose fetch has not landed — or failed — is left exactly as it
 * was, so the view degrades to what it showed before rather than to a hole.
 *
 * @param {unknown} value
 * @param {Map<string, unknown>} resolved blob id to its content
 * @returns {unknown}
 */
export function withResolvedBlobs(value, resolved) {
  if (Array.isArray(value)) {
    return value.map((item) => withResolvedBlobs(item, resolved));
  }
  if (typeof value !== "object" || value === null) {
    return value;
  }
  if (isStored(value) && resolved.has(value.value.blob_id)) {
    return {
      state: "inline",
      value: resolved.get(value.value.blob_id),
      // Kept, because the difference between "this arrived inline" and "this
      // was fetched" is the difference between a small payload and a large
      // one, and a reader chasing latency wants to know which.
      fetched_from: value.value,
    };
  }
  const copy = {};
  for (const [key, item] of Object.entries(value)) {
    copy[key] = withResolvedBlobs(item, resolved);
  }
  return copy;
}

/** Applies `visit` to every stored reference in `value`. */
function walk(value, visit) {
  if (Array.isArray(value)) {
    for (const item of value) {
      walk(item, visit);
    }
    return;
  }
  if (typeof value !== "object" || value === null) {
    return;
  }
  if (isStored(value)) {
    visit(value);
    return;
  }
  for (const item of Object.values(value)) {
    walk(item, visit);
  }
}
